"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { ColumnDef } from '@tanstack/react-table'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { DataTable, withDataTableNamespaces } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { TruncatedCell } from '@open-mercato/ui/backend/TruncatedCell'
import { BooleanIcon, EnumBadge, type EnumBadgeMap } from '@open-mercato/ui/backend/ValueIcons'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import {
  formatParticipantTypeLabel,
  formatSupportedModeLabel,
  formatSupportedSegmentLabel,
  participantTypeValues,
  supportedModeValues,
  supportedSegmentValues,
} from '../../lib/participantProfile'

type ParticipantRow = {
  id: string
  customerEntityId: string | null
  customerDisplayName: string | null
  customerPrimaryEmail: string | null
  customerPrimaryPhone: string | null
  participantType: string | null
  supportedModes: string[]
  supportedSegments: string[]
  nip: string | null
  vatEu: string | null
  eori: string | null
  isVerified: boolean
  isActive: boolean
  fleetTrucks: number
  fleetTrailers: number
  fleetLocomotives: number
  fleetWagons: number
  createdAt: string | null
  updatedAt: string | null
} & Record<string, unknown>

type ParticipantsResponse = {
  items?: Array<Record<string, unknown>>
  total?: number
  page?: number
  totalPages?: number
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

function mapApiItem(item: Record<string, unknown>): ParticipantRow | null {
  const id = normalizeText(item.id)
  if (!id) return null
  return withDataTableNamespaces({
    id,
    customerEntityId: normalizeText(item.customerEntityId),
    customerDisplayName: normalizeText(item.customerDisplayName),
    customerPrimaryEmail: normalizeText(item.customerPrimaryEmail),
    customerPrimaryPhone: normalizeText(item.customerPrimaryPhone),
    participantType: normalizeText(item.participantType),
    supportedModes: normalizeStringArray(item.supportedModes),
    supportedSegments: normalizeStringArray(item.supportedSegments),
    nip: normalizeText(item.nip),
    vatEu: normalizeText(item.vatEu),
    eori: normalizeText(item.eori),
    isVerified: item.isVerified === true,
    isActive: item.isActive !== false,
    fleetTrucks: normalizeNumber(item.fleetTrucks),
    fleetTrailers: normalizeNumber(item.fleetTrailers),
    fleetLocomotives: normalizeNumber(item.fleetLocomotives),
    fleetWagons: normalizeNumber(item.fleetWagons),
    createdAt: normalizeText(item.createdAt),
    updatedAt: normalizeText(item.updatedAt),
  }, item)
}

function formatFleetSummary(row: ParticipantRow): string {
  const parts = [
    row.fleetTrucks > 0 ? `T ${row.fleetTrucks}` : null,
    row.fleetTrailers > 0 ? `Tr ${row.fleetTrailers}` : null,
    row.fleetLocomotives > 0 ? `L ${row.fleetLocomotives}` : null,
    row.fleetWagons > 0 ? `W ${row.fleetWagons}` : null,
  ].filter((value): value is string => Boolean(value))
  return parts.length > 0 ? parts.join(' • ') : '—'
}

function renderTokenBadges(values: string[], formatter: (value: string) => string) {
  if (!values.length) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {values.slice(0, 3).map((value) => (
        <Badge key={value} variant="secondary" className="text-[11px]">
          {formatter(value)}
        </Badge>
      ))}
      {values.length > 3 ? <Badge variant="outline" className="text-[11px]">+{values.length - 3}</Badge> : null}
    </div>
  )
}

export default function ParticipantsPage() {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const [rows, setRows] = React.useState<ParticipantRow[]>([])
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(0)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)

  const participantTypeMap = React.useMemo<EnumBadgeMap>(() => (
    Object.fromEntries(
      participantTypeValues.map((value) => [
        value,
        {
          label: t(`participants.types.${value.toLowerCase()}`, formatParticipantTypeLabel(value)),
          className: 'border-sky-200 bg-sky-50 text-sky-700',
        },
      ]),
    )
  ), [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'participantType',
      label: t('participants.list.filters.participantType', 'Participant type'),
      type: 'select',
      options: participantTypeValues.map((value) => ({
        value,
        label: t(`participants.types.${value.toLowerCase()}`, formatParticipantTypeLabel(value)),
      })),
    },
    {
      id: 'mode',
      label: t('participants.list.filters.mode', 'Mode'),
      type: 'select',
      options: supportedModeValues.map((value) => ({
        value,
        label: t(`participants.modes.${value.toLowerCase()}`, formatSupportedModeLabel(value)),
      })),
    },
    {
      id: 'segment',
      label: t('participants.list.filters.segment', 'Segment'),
      type: 'select',
      options: supportedSegmentValues.map((value) => ({
        value,
        label: t(`participants.segments.${value.toLowerCase()}`, formatSupportedSegmentLabel(value)),
      })),
    },
    {
      id: 'isVerified',
      label: t('participants.list.filters.verified', 'Verification'),
      type: 'select',
      options: [
        { value: 'true', label: t('participants.list.filters.verifiedOnly', 'Verified only') },
        { value: 'false', label: t('participants.list.filters.unverifiedOnly', 'Unverified only') },
      ],
    },
  ], [t])

  const columns = React.useMemo<ColumnDef<ParticipantRow>[]>(() => [
    {
      accessorKey: 'customerDisplayName',
      header: t('participants.list.columns.customer', 'CRM company'),
      cell: ({ row }) => (
        <div className="space-y-1">
          <TruncatedCell>
            {row.original.customerDisplayName ?? t('participants.list.fallbacks.missingCustomer', 'Missing company')}
          </TruncatedCell>
          {row.original.customerPrimaryEmail ? (
            <div className="text-xs text-muted-foreground">{row.original.customerPrimaryEmail}</div>
          ) : null}
        </div>
      ),
      meta: { maxWidth: '18rem', truncate: true },
    },
    {
      accessorKey: 'participantType',
      header: t('participants.list.columns.participantType', 'Type'),
      cell: ({ row }) => <EnumBadge value={row.original.participantType} map={participantTypeMap} fallback="—" />,
    },
    {
      accessorKey: 'supportedModes',
      header: t('participants.list.columns.supportedModes', 'Modes'),
      cell: ({ row }) => renderTokenBadges(row.original.supportedModes, formatSupportedModeLabel),
      meta: { maxWidth: '14rem' },
    },
    {
      accessorKey: 'supportedSegments',
      header: t('participants.list.columns.supportedSegments', 'Segments'),
      cell: ({ row }) => renderTokenBadges(row.original.supportedSegments, formatSupportedSegmentLabel),
      meta: { maxWidth: '16rem' },
    },
    {
      accessorKey: 'identifiers',
      header: t('participants.list.columns.identifiers', 'Identifiers'),
      cell: ({ row }) => (
        <div className="space-y-1 text-xs">
          <div>{row.original.nip ? `NIP ${row.original.nip}` : '—'}</div>
          <div className="text-muted-foreground">
            {[row.original.vatEu ? `VAT-EU ${row.original.vatEu}` : null, row.original.eori ? `EORI ${row.original.eori}` : null]
              .filter((value): value is string => Boolean(value))
              .join(' • ') || '—'}
          </div>
        </div>
      ),
      meta: { maxWidth: '15rem' },
    },
    {
      accessorKey: 'isVerified',
      header: t('participants.list.columns.verified', 'Verified'),
      cell: ({ row }) => <BooleanIcon value={row.original.isVerified} />,
    },
    {
      accessorKey: 'fleetSummary',
      header: t('participants.list.columns.fleet', 'Fleet'),
      cell: ({ row }) => <span className="text-sm">{formatFleetSummary(row.original)}</span>,
    },
  ], [participantTypeMap, t])

  React.useEffect(() => {
    let cancelled = false

    async function load() {
      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })
        if (search.trim()) params.set('search', search.trim())
        Object.entries(filterValues).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim().length > 0) params.set(key, value)
        })
        const payload = await readApiResultOrThrow<ParticipantsResponse>(
          `/api/participants?${params.toString()}`,
          undefined,
          { errorMessage: t('participants.list.loadError', 'Failed to load participants.') },
        )
        if (cancelled) return
        const mappedRows = (Array.isArray(payload.items) ? payload.items : [])
          .map((item) => mapApiItem(item))
          .filter((item): item is ParticipantRow => item !== null)
        setRows(mappedRows)
        setTotal(typeof payload.total === 'number' ? payload.total : mappedRows.length)
        setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 0)
      } catch (error) {
        if (cancelled) return
        const message = error instanceof Error && error.message ? error.message : t('participants.list.loadError', 'Failed to load participants.')
        flash(message, 'error')
        setRows([])
        setTotal(0)
        setTotalPages(0)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [filterValues, page, pageSize, reloadToken, scopeVersion, search, t])

  const handleDelete = React.useCallback(async (row: ParticipantRow) => {
    const fallbackName = t('participants.list.deleteFallback', 'this participant')
    const confirmed = await confirm({
      title: t('participants.list.deleteDialog.title', 'Delete participant profile?'),
      text: t(
        'participants.list.deleteDialog.description',
        'This removes the freight profile but leaves the linked CRM company untouched.',
      ),
      confirmText: t('participants.list.deleteDialog.confirm', 'Delete'),
      cancelText: t('participants.list.deleteDialog.cancel', 'Cancel'),
      variant: 'destructive',
    })
    if (!confirmed) return
    await deleteCrud('participants', row.id)
    flash(
      t('participants.list.deleteSuccess', 'Participant profile deleted: {{name}}', {
        name: row.customerDisplayName ?? fallbackName,
      }),
      'success',
    )
    setReloadToken((current) => current + 1)
  }, [confirm, t])

  return (
    <Page>
      <PageHeader
        title={t('participants.list.title', 'Participants')}
        description={t(
          'participants.list.description',
          'Manage freight-specific profiles that extend existing CRM companies with operational and financial context.',
        )}
        actions={(
          <Button asChild>
            <Link href="/backend/participants/create">{t('participants.list.actions.create', 'Create participant')}</Link>
          </Button>
        )}
      />
      <PageBody>
        <DataTable
          title={t('participants.list.tableTitle', 'Participant profiles')}
          columns={columns}
          data={rows}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(next) => {
            setFilterValues(next)
            setPage(1)
          }}
          onFiltersClear={() => {
            setFilterValues({})
            setPage(1)
          }}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          searchPlaceholder={t('participants.list.searchPlaceholder', 'Search by company, identifiers, modes, or type')}
          perspective={{ tableId: 'participants.list' }}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
          onRowClick={(row) => router.push(`/backend/participants/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'open',
                  label: t('participants.list.actions.open', 'Open'),
                  onSelect: () => router.push(`/backend/participants/${row.id}`),
                },
                {
                  id: 'delete',
                  label: t('participants.list.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
